import Layout from "components/Layout";
import { graphQlClient } from "lib/graphql-client.js";
import { destinationsCardQuery } from "lib/graphql/destination-queries";

import { site } from "config";
import { fetchExperiencesByRegion } from "lib/api/strapi-experiences";
import dynamic from "next/dynamic";

import PageIntro from "components/content/PageIntro/PageIntro";
const Tabs = dynamic(() => import("components/ui/Tabs/Tabs"));
const Tab = dynamic(() => import("components/ui/Tabs/Tab"));
const CardRow = dynamic(() => import("components/card/CardRow"));
const DestinationLinkCard = dynamic(
  () => import("components/card/DestinationLinkCard")
);
const RelatedExperiences = dynamic(
  () =>
    import(
      "components/ExperiencePresentation/RelatedExperiences/RelatedExperiences"
    )
);

import { fetchLayoutData, fetchLayoutDataServer } from "lib/api/strapi-layout";

import { processDestinationData } from "lib/api/strapi-destinations";
import styles from "./RegionSlug.module.scss";

const RegionMain = ({
  region,
  destinations,
  relatedExperiences,
  layoutData,
}) => {
  const SEO = {
    ...region?.seo,
    metaTitle:
      region?.seo?.metaTitle ?? `WhereNext | Destination ${region.regionName}`,
    canonicalURL: site + "/destinations" + "/" + region.region,
  };

  const { page_intro } = region;

  const destinationRows = (
    <CardRow
      cards={destinations?.map((item, index) => {
        return <DestinationLinkCard destination={item} key={index} />;
      })}
      seeAll={false}
      sectionSlug="/explore-travel-destinations"
    />
  );

  return (
    <Layout
      seo={SEO}
      customStyles={{ background: "var(--light-background-3)" }}
      layoutData={layoutData}
    >
      <div className={`variable-container ${styles.regionContainer}`}>
        <PageIntro
          title={page_intro?.page_title ?? region.regionName}
          description={page_intro?.page_introduction}
          image={page_intro?.intro_photo?.url}
          layout="imageRight"
        />

        <Tabs tabButtonStyle="mini">
          <Tab title="Destinations">{destinationRows}</Tab>
          {relatedExperiences.length > 0 && (
            <Tab title="Experiences">
              <RelatedExperiences
                experiences={relatedExperiences}
                sideScroll={null}
              />
            </Tab>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default RegionMain;

export async function getStaticProps(content) {
  const slug = content.params.slug;

  const { gql } = await import("@apollo/client");

  const strapiApolloClient = graphQlClient();

  let { data } = await strapiApolloClient.query({
    query: destinationsCardQuery,
    variables: {
      regions: [slug],
      limit: -1,
    },
  });

  const destinations = data.destinations.data.map((destination) => {
    return processDestinationData(destination, { type: "basic" });
  });

  let regionResponse = await strapiApolloClient.query({
    query: gql`
      query regionsQuery($region: String) {
        regions(filters: { region: { eq: $region } }) {
          data {
            id
            attributes {
              region
              regionName
              page_intro {
                page_title
                page_introduction
                intro_photo {
                  data {
                    attributes {
                      url
                      alternativeText
                    }
                  }
                }
              }
              search_engine_optimization {
                id
                metaTitle
                metaDescription
                preventIndexing
                metaMedia {
                  data {
                    attributes {
                      url
                      alternativeText
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      region: slug,
    },
  });

  const regionData = regionResponse.data.regions.data[0].attributes;

  const region = {
    id: regionResponse.data.regions.data[0].id,
    region: regionData.region,
    regionName: regionData.regionName,
    seo: {
      ...regionData.search_engine_optimization,
      metaMedia: {
        ...regionData.search_engine_optimization.metaMedia.data.attributes,
      },
    },
    page_intro: {
      ...regionData.page_intro,
      intro_photo: { ...regionData.page_intro.intro_photo.data.attributes },
    },
  };

  const relatedExperiences = await fetchExperiencesByRegion([slug], {
    limit: -1,
  });

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();
  return {
    props: {
      slug,
      region,
      destinations,
      relatedExperiences: relatedExperiences ?? [],
      key: slug,
      layoutData: layoutData,
    },
  };
}

export async function getStaticPaths() {
  const { gql } = await import("@apollo/client");

  const strapiApolloClient = graphQlClient();

  let { data } = await strapiApolloClient.query({
    query: gql`
      query regionsQuery {
        regions {
          data {
            attributes {
              region
            }
          }
        }
      }
    `,
  });

  const paths =
    data.regions.data.length > 0 &&
    data?.regions.data.map((region) => {
      return {
        params: { slug: region.attributes.region.toString() },
      };
    });

  return {
    paths,
    fallback: false,
  };
}
